import { Link, useLocation } from "react-router-dom";
import { cn } from "@jambushrushti/lib/utils";
import { useAdminAuth } from "@jambushrushti/contexts/AdminAuthContext";
import {
  LayoutDashboard,
  Plus,
  List,
  Settings,
  LogOut,
  Ticket,
  X,
  BarChart3,
  Clock,
  Tag,
} from "lucide-react";
import { Button } from "@jambushrushti/components/ui/button";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@jambushrushti/components/ui/avatar";

const navItems = [
  { icon: LayoutDashboard, label: "Dashboard", href: "/tickets/dashboard" },
  { icon: Plus, label: "Create Ticket", href: "/tickets/create" },
  { icon: List, label: "All Tickets", href: "/tickets/list" },
  { icon: Clock, label: "My Tickets", href: "/tickets/my-tickets" },
  { icon: Tag, label: "Categories", href: "/tickets/categories" },
  { icon: BarChart3, label: "Reports", href: "/tickets/reports" },
  { icon: Settings, label: "SLA Settings", href: "/tickets/sla-settings" },
];

interface TicketSidebarProps {
  onClose?: () => void;
}

export function TicketSidebar({ onClose }: TicketSidebarProps) {
  const location = useLocation();
  const { user, logout } = useAdminAuth();

  const handleLogout = async () => {
    await logout();
    window.location.href = "/auth";
  };

  return (
    <aside className="flex min-h-screen w-72 flex-col bg-primary text-primary-foreground lg:w-64">
      {/* Logo/Brand */}
      <div className="flex items-center justify-between border-b border-primary-foreground/10 p-4 sm:p-6">
        <Link
          to="/tickets/dashboard"
          className="flex items-center gap-3"
          onClick={onClose}
        >
          <div className="bg-orange/20 flex h-10 w-10 items-center justify-center rounded-xl">
            <Ticket className="text-orange h-6 w-6" />
          </div>
          <div>
            <h1 className="font-heading text-lg font-bold text-white">
              Ticket System
            </h1>
            <p className="text-xs text-white/60">Jambushrusti</p>
          </div>
        </Link>
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            className="text-white/70 hover:bg-white/10 hover:text-white lg:hidden"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* User Info */}
      <div className="border-b border-primary-foreground/10 p-4">
        <div className="flex items-center gap-3">
          <Avatar className="border-orange/30 h-10 w-10 border-2">
            <AvatarImage src={user?.avatar} />
            <AvatarFallback className="bg-orange/20 text-orange font-semibold">
              {user?.name?.charAt(0).toUpperCase() || "A"}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white">
              {user?.name || "Admin"}
            </p>
            <p className="truncate text-xs text-white/60">Support Admin</p>
          </div>
        </div>
      </div>

      {/* Role Badge */}
      <div className="px-4 py-2">
        <div className="bg-orange/20 text-orange rounded-lg px-3 py-1.5 text-center text-xs font-medium">
          Ticket Administrator
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 overflow-auto p-3 sm:p-4">
        {navItems.map(item => {
          const isActive =
            location.pathname === item.href ||
            (item.href !== "/tickets/dashboard" &&
              location.pathname.startsWith(item.href));

          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={onClose}
              className={cn(
                "flex items-center gap-3 rounded-xl px-4 py-3 transition-all duration-200",
                isActive
                  ? "bg-orange font-medium text-white shadow-lg"
                  : "text-white/70 hover:bg-white/10 hover:text-white"
              )}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Back to Admin */}
      <div className="border-t border-primary-foreground/10 p-4">
        <Link
          to="/vault-cms-admin/dashboard"
          onClick={onClose}
          className="flex items-center gap-3 rounded-xl px-4 py-3 text-white/70 transition-all duration-200 hover:bg-white/10 hover:text-white"
        >
          <LayoutDashboard className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm">Back to Admin</span>
        </Link>
      </div>

      {/* Logout */}
      <div className="border-t border-primary-foreground/10 p-4">
        <Button
          variant="ghost"
          className="w-full justify-start text-white/70 hover:bg-white/10 hover:text-white"
          onClick={handleLogout}
        >
          <LogOut className="mr-3 h-5 w-5" />
          Sign Out
        </Button>
      </div>
    </aside>
  );
}
